import { useQueryClient } from "@tanstack/react-query"
import { useSharedTimeCapsuleBySlug } from "api/ApiHooks"
import { LoversApi } from "api/LoversApi"
import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

const LoadingView = () => {
  return (
    <main className="py-10 lg:pl-72">
      <div className="px-4 sm:px-6 lg:px-8">
        <p>Loading...</p>
      </div>
    </main>
  )
}

export const MemberTimeCapsuleSettingsPage = () => {
  const { creatorId, slug } = useParams()
  const { data, isLoading } = useSharedTimeCapsuleBySlug(creatorId || "", slug || "")

  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmName, setConfirmName] = useState<string>("")
  const [error, setError] = useState<string>()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsDeleting(true)
    if (!data?.id) return

    LoversApi()
      .timeCapsule.leaveTimeCapsule(data.id)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["useSharedTimeCapsuleBySlug", creatorId, slug]
        })
        queryClient.invalidateQueries({ queryKey: ["useSharedTimeCapsules"] })
        navigate("/dashboard")
      })
      .catch((err) => {
        setError(err.response.data.message)
      })
      .finally(() => {
        setIsDeleting(false)
      })
  }

  return (
    <>
      {isLoading || !data ? (
        <LoadingView />
      ) : (
        <div className="pb-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="space-y-12">
              <div className="pb-12">
                <div className="mb-10">
                  <h2 className="font-semibold text-lg text-gray-900">
                    {data.name} settings
                  </h2>
                  <p className="mt-1 text-sm/6 text-gray-600">
                    Here are your membership settings for this time capsule.
                  </p>
                </div>

                <div className="border-t border-gray-900/10 pt-12 pb-2">
                  <div className="space-y-8 p-4 rounded-lg shadow-md border-2 border-red-600 bg-red-50 sm:px-6">
                    <div>
                      <h1 className="text-3xl font-semibold text-red-500 mb-3">
                        Danger zone
                      </h1>
                      <h2 className="text-base font-semibold text-gray-900">
                        Leave this time capsule
                      </h2>
                      <p className="mt-1 text-sm text-gray-900">
                        This action cannot be undone. You existing entries will stay in
                        the capsule but your access to it will be removed. To confirm and
                        leave, please type the time capsule name:{" "}
                        <span className="font-semibold">{data.name}</span>
                      </p>
                    </div>

                    <form onSubmit={handleDelete} className="space-y-6">
                      <div>
                        <input
                          type="text"
                          value={confirmName}
                          onChange={(e) => setConfirmName(e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-red-600 sm:text-sm sm:leading-6"
                          placeholder="Enter time capsule name"
                        />
                      </div>

                      {error && <p className="text-sm text-red-500">{error}</p>}

                      <button
                        type="submit"
                        disabled={confirmName !== data.name || isDeleting}
                        className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:opacity-50 disabled:hover:bg-red-600"
                      >
                        {isDeleting ? "Leaving..." : "Leave time capsule"}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
